/** Public summaries of stored fitment evidence; never a certification of fit. */
export interface FilterFitmentEvidence {
  basis: 'source_record' | 'aftermarket_cross_reference' | 'unknown'
  sourceHost: string | null
  recordedAt: string | null
}

export interface FilterCoverage {
  examined: number
  returned: number
  excluded: number
  hasMore: boolean
}

export function filterFitmentEvidence(source: unknown, recordedAt: unknown, now = Date.now()): FilterFitmentEvidence {
  const text = typeof source === 'string' ? source.trim().slice(0, 8192) : ''
  let sourceHost: string | null = null
  const url = text.match(/https?:\/\/[^\s)]+/i)?.[0]
  if (url) {
    try {
      const host = new URL(url).hostname.toLowerCase()
      if (/^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(host) && !/\.(?:internal|localhost|local|test|invalid|example)$/.test(host)) sourceHost = host
    } catch { /* A malformed source remains unknown; never emit raw source text. */ }
  }
  const date = typeof recordedAt === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(recordedAt) ? Date.parse(recordedAt) : NaN
  return {
    basis: !text ? 'unknown' : /^aftermarket-cross-reference\b/i.test(text) ? 'aftermarket_cross_reference' : 'source_record',
    sourceHost,
    recordedAt: Number.isFinite(date) && date <= now ? new Date(date).toISOString() : null,
  }
}

export function filterCoverageNote(coverage?: FilterCoverage | null): string {
  if (!coverage) return 'Coverage is unknown. This result is not an exhaustive list of compatible filters.'
  return [
    `${coverage.returned} filter listing${coverage.returned === 1 ? '' : 's'} shown from ${coverage.examined} examined compatibility records.`,
    coverage.excluded ? `${coverage.excluded} record${coverage.excluded === 1 ? '' : 's'} could not be shown because the linked product is unavailable or ineligible for this finder.` : '',
    coverage.hasMore ? 'More stored records exist; this result is partial.' : '',
    'Stored records are not an exhaustive compatibility list.',
  ].filter(Boolean).join(' ')
}

export function filterFitmentNote(evidence?: FilterFitmentEvidence | null): string {
  const basis = evidence?.basis === 'aftermarket_cross_reference' ? 'Aftermarket cross-reference' : evidence?.basis === 'source_record' ? 'Stored source record' : 'Source evidence unavailable'
  const host = evidence?.sourceHost ? ` (${evidence.sourceHost})` : ''
  const date = evidence?.recordedAt ? `Record dated ${evidence.recordedAt.slice(5, 7)}/${evidence.recordedAt.slice(8, 10)}/${evidence.recordedAt.slice(0, 4)}.` : 'No verification date recorded.'
  return `${basis}${host}. ${date} Confirm the full appliance model and filter number with the manufacturer; this is not a fit or certification guarantee.`
}
